import { useEffect, useRef } from 'react';
import { getEchoInstance, initializeEcho } from '../echoService';
import { useUserContext } from '../../context/UserContext';
import { NOTIFICATION_RECEIVED_EVENT } from './eventsNames';
import { useEchoNotification } from '@laravel/echo-react';

/**
 * Generic broadcast listener hook to subscribe to a channel and listen to an event.
 * It abstracts away Laravel Echo setup, teardown, and prevents stale closures on callbacks.
 *
 * @param {Object} options 
 * @param {string} options.channelName - The name of the channel to subscribe to.
 * @param {boolean} options.isPrivate - Whether it is a private channel (requires authentication).
 * @param {Object} [options.events] - An object mapping event names to handler functions.
 * @param {Function} [options.onNotification] - Callback for Laravel's built-in notification channel method.
 * @param {boolean} [options.enabled=true] - Whether the listener is currently active.
 */
export function useBroadcastListener({ channelName, isPrivate, events = {}, onNotification, enabled = true }) {
  // Use a ref for the handlers so we always have the latest closure without re-subscribing.
  const savedEvents = useRef(events);
  const savedOnNotification = useRef(onNotification);

  useEffect(() => {
    savedEvents.current = events;
    savedOnNotification.current = onNotification;
  }, [events, onNotification]);

  useEffect(() => {
    if (!enabled || !channelName) return;

    let echo = getEchoInstance();
    if (!echo) {
      let value = JSON.parse(localStorage.getItem("doctolikLoginInfo"));
      const token = value?.authorisation?.token;
      if (token) {
        echo = initializeEcho(token);
      } else {
        console.warn("[BroadcastService] Cannot initialize Echo without a token.");
        return;
      }
    }

    const channel = isPrivate ? echo.private(channelName) : echo.channel(channelName);

    console.debug(`[BroadcastService] Subscribing to ${channelName}`);

    // Attach regular event listeners
    const eventNames = Object.keys(savedEvents.current || {});
    eventNames.forEach(eventName => {
      channel.listen(eventName, (payload) => {
        if (savedEvents.current[eventName]) {
          savedEvents.current[eventName](payload);
        }
      });
    });

    // Attach notification listener if provided
    if (onNotification) {
      console.log(`[BroadcastService] Listening for notifications on ${channelName}`);
      channel.notification((notification) => {
        if (savedOnNotification.current) {
          savedOnNotification.current(notification);
        }
      });
    }

    return () => {
      console.debug(`[BroadcastService] Unsubscribing from ${channelName}`);
      eventNames.forEach(eventName => {
         // Laravel Echo's stopListening doesn't strictly require the exact callback reference 
         // if you just want to stop listening to the event entirely on this component unmount, 
         // but leaving it empty unbinds all for this event. 
         // For a safer targeted unbind, we would need to store the exact wrapped callbacks.
         // However, since we unsubscribe from the channel entirely or stop listening, it's fine.
         channel.stopListening(eventName);
      });
      // Currently echo doesn't have a stopNotification method, it's tied to the channel instance.
      // Usually you would just echo.leave(channelName) if you want to completely disconnect,
      // but since others might be on this channel, stopListening is best effort.
    };
  }, [channelName, isPrivate, enabled]);
}

/**
 * Specialized domain listener for User Notifications.
 * Subscribes to the default Laravel notification private channel for the current user.
 *
 * @param {Object} options
 * @param {boolean} [options.enabled=true]
 * @param {Function} options.onEvent
 */
export function useNotificationsListener({ enabled = true, onEvent }) {
  const { userData } = useUserContext();
  const userId = userData?.users?.id;

  // Default Laravel behavior broadcasts notifications to `users.{id}.notifications` private channel.
  const channelName = `users.${userId}.notifications`;

  useBroadcastListener({
    channelName,
    isPrivate: true,
    enabled: enabled && !!userId,
    onNotification: onEvent
  });
}
